import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { LoadingSpinner } from '@/presentation/components/common/LoadingSpinner';
import {
  createEmailContact,
  deleteEmailContact,
  listClinicTeamRecipients,
  listEmailContacts,
  type ClinicTeamRecipient,
  type EmailContact,
} from '@/infrastructure/http/contactsApi';

export interface PickedEmailRecipient {
  /** id de agenda si existe; si no, email como clave temporal. */
  key: string;
  nombre: string;
  email: string;
  fromAgenda: boolean;
  source?: 'owner' | 'assistant' | 'agenda' | 'typed';
}

interface EmailContactPickerProps {
  token: string;
  selected: PickedEmailRecipient[];
  onChange: (next: PickedEmailRecipient[]) => void;
  disabled?: boolean;
  /** Clínica del reporte: filtra el equipo con acceso. */
  clinicId?: string;
  /** Prefill con dueño + asistentes de esa clínica al montar. */
  preselectTeam?: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

function toPicked(contact: EmailContact): PickedEmailRecipient {
  return {
    key: contact.id,
    nombre: contact.nombre,
    email: contact.email,
    fromAgenda: true,
    source: 'agenda',
  };
}

function teamToPicked(member: ClinicTeamRecipient): PickedEmailRecipient {
  return {
    key: member.assistantId ? `assistant:${member.assistantId}` : `owner:${member.email}`,
    nombre: member.nombre,
    email: member.email,
    fromAgenda: false,
    source: member.role,
  };
}

/**
 * Picker de agenda estilo Coofit: equipo de la clínica a mano, búsqueda, chips y alta rápida.
 */
export function EmailContactPicker({
  token,
  selected,
  onChange,
  disabled,
  clinicId,
  preselectTeam = false,
}: EmailContactPickerProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const preselectedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [contacts, setContacts] = useState<EmailContact[]>([]);
  const [team, setTeam] = useState<ClinicTeamRecipient[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newNombre, setNewNombre] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [focused, setFocused] = useState(false);

  const selectedEmails = useMemo(
    () => new Set(selected.map((r) => r.email.toLowerCase())),
    [selected]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    preselectedRef.current = false;
  }, [clinicId, token]);

  useEffect(() => {
    let cancelled = false;
    setTeamLoading(true);
    void listClinicTeamRecipients(token, clinicId)
      .then((rows) => {
        if (cancelled) return;
        setTeam(rows);
        if (preselectTeam && !preselectedRef.current && rows.length > 0) {
          preselectedRef.current = true;
          onChangeRef.current(rows.map(teamToPicked));
        }
      })
      .catch(() => {
        if (!cancelled) setTeam([]);
      })
      .finally(() => {
        if (!cancelled) setTeamLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, clinicId, preselectTeam]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listEmailContacts(token, debouncedSearch || undefined)
      .then((rows) => {
        if (!cancelled) setContacts(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          setContacts([]);
          setError(err instanceof Error ? err.message : 'contacts_request_failed');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, debouncedSearch]);

  const suggestions = useMemo(
    () => contacts.filter((c) => !selectedEmails.has(c.email.toLowerCase())).slice(0, 12),
    [contacts, selectedEmails]
  );

  const teamAvailable = useMemo(
    () => team.filter((m) => !selectedEmails.has(m.email.toLowerCase())),
    [team, selectedEmails]
  );

  const typedLooksLikeEmail = EMAIL_REGEX.test(searchInput.trim());
  const canQuickAdd =
    typedLooksLikeEmail && !selectedEmails.has(searchInput.trim().toLowerCase());

  function addRecipient(recipient: PickedEmailRecipient) {
    if (selectedEmails.has(recipient.email.toLowerCase())) return;
    onChange([...selected, recipient]);
    setSearchInput('');
    setShowCreate(false);
  }

  function removeRecipient(key: string) {
    onChange(selected.filter((r) => r.key !== key));
  }

  function addTypedEmail() {
    const email = searchInput.trim().toLowerCase();
    if (!EMAIL_REGEX.test(email)) return;
    addRecipient({
      key: `typed:${email}`,
      nombre: email.split('@')[0] || email,
      email,
      fromAgenda: false,
      source: 'typed',
    });
  }

  async function handleCreate() {
    const nombre = newNombre.trim() || newEmail.trim().split('@')[0] || 'Contacto';
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_REGEX.test(email)) {
      setError('contact_email_invalid');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const created = await createEmailContact(token, { nombre, email });
      addRecipient(toPicked(created));
      setNewNombre('');
      setNewEmail('');
      setShowCreate(false);
      setContacts((prev) => [created, ...prev.filter((c) => c.id !== created.id)]);
    } catch (err) {
      const code = err instanceof Error ? err.message : 'contacts_request_failed';
      setError(code);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(contactId: string) {
    try {
      await deleteEmailContact(token, contactId);
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
      onChange(selected.filter((r) => r.key !== contactId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'contacts_request_failed');
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t('contacts.picker.title')}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setShowCreate((v) => !v);
            setNewEmail(EMAIL_REGEX.test(searchInput.trim()) ? searchInput.trim() : '');
          }}
          className="text-xs font-medium text-teal-700 hover:underline disabled:opacity-50 dark:text-teal-300"
        >
          {showCreate ? t('contacts.picker.cancelCreate') : t('contacts.picker.create')}
        </button>
      </div>

      {(team.length > 0 || teamLoading) && (
        <div className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50/80 p-2.5 dark:border-slate-700 dark:bg-slate-900/40">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {clinicId ? t('contacts.picker.teamTitle') : t('contacts.picker.teamTitleAll')}
            </span>
            {teamAvailable.length > 0 ? (
              <button
                type="button"
                disabled={disabled}
                onClick={() =>
                  onChange([
                    ...selected,
                    ...teamAvailable.map(teamToPicked),
                  ])
                }
                className="text-xs font-medium text-teal-700 hover:underline disabled:opacity-50 dark:text-teal-300"
              >
                {t('contacts.picker.addAllTeam')}
              </button>
            ) : null}
          </div>
          {teamLoading ? (
            <LoadingBlock size="sm" minHeightClassName="min-h-[3rem]" />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {team.map((m) => {
                const picked = selectedEmails.has(m.email.toLowerCase());
                return (
                  <button
                    key={`${m.role}:${m.email}`}
                    type="button"
                    disabled={disabled}
                    title={m.email}
                    onClick={() => {
                      if (picked) {
                        onChange(selected.filter((r) => r.email.toLowerCase() !== m.email.toLowerCase()));
                      } else {
                        addRecipient(teamToPicked(m));
                      }
                    }}
                    className={`inline-flex max-w-full items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm transition ${
                      picked
                        ? 'border-teal-300 bg-teal-50 text-teal-900 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-200'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-teal-300 hover:bg-teal-50/50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
                    }`}
                  >
                    <span className="max-w-[120px] truncate">{m.nombre}</span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wide opacity-60">
                      {m.role === 'owner'
                        ? t('contacts.picker.roleOwner')
                        : t('contacts.picker.roleAssistant')}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-slate-400 dark:text-slate-500">{t('contacts.picker.teamHint')}</p>
        </div>
      )}

      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((r) => (
            <button
              key={r.key}
              type="button"
              disabled={disabled}
              onClick={() => removeRecipient(r.key)}
              title={r.email}
              className="inline-flex items-center gap-1.5 rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1 text-sm text-teal-900 dark:border-teal-800 dark:bg-teal-900/20 dark:text-teal-200"
            >
              <span className="max-w-[140px] truncate">{r.nombre}</span>
              <span className="opacity-60" aria-hidden>
                ×
              </span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            window.setTimeout(() => setFocused(false), 180);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canQuickAdd) addTypedEmail();
              else if (suggestions[0]) addRecipient(toPicked(suggestions[0]));
            }
          }}
          placeholder={t('contacts.picker.searchPlaceholder')}
          autoComplete="off"
          className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
        />

        {focused && (searchInput.trim().length >= 1 || contacts.length > 0 || loading) ? (
          <div className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <div className="max-h-52 overflow-y-auto">
              {loading ? (
                <LoadingBlock size="sm" minHeightClassName="min-h-[3rem]" />
              ) : (
                <>
                  {canQuickAdd ? (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={addTypedEmail}
                      className="w-full border-b border-gray-100 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
                    >
                      <span className="block text-sm font-medium text-teal-700 dark:text-teal-300">
                        {t('contacts.picker.useEmail', { email: searchInput.trim() })}
                      </span>
                    </button>
                  ) : null}
                  {suggestions.length === 0 && !canQuickAdd ? (
                    <p className="px-3 py-3 text-sm text-gray-500">
                      {debouncedSearch
                        ? t('contacts.picker.emptySearch')
                        : t('contacts.picker.emptyAgenda')}
                    </p>
                  ) : (
                    suggestions.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-stretch border-b border-gray-50 last:border-0 dark:border-gray-700/60"
                      >
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => addRecipient(toPicked(c))}
                          className="min-w-0 flex-1 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                          <span className="block truncate text-sm text-gray-900 dark:text-white">
                            {c.nombre}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
                            {c.email}
                          </span>
                        </button>
                        <button
                          type="button"
                          title={t('contacts.picker.delete')}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => void handleDelete(c.id)}
                          className="px-2.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {showCreate ? (
        <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/50">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
            {t('contacts.picker.createHint')}
          </p>
          <input
            type="text"
            value={newNombre}
            onChange={(e) => setNewNombre(e.target.value)}
            placeholder={t('contacts.picker.namePlaceholder')}
            className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-950 dark:text-white"
          />
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={t('contacts.picker.emailPlaceholder')}
            className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-950 dark:text-white"
          />
          <button
            type="button"
            disabled={creating || !newEmail.trim()}
            onClick={() => void handleCreate()}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
          >
            {creating ? (
              <>
                <LoadingSpinner size="sm" className="border-white/30 border-t-white" />
                {t('common.loading')}
              </>
            ) : (
              t('contacts.picker.saveContact')
            )}
          </button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400">
          {t(`contacts.errors.${error}`, { defaultValue: t('contacts.errors.generic') })}
        </p>
      ) : null}

      <p className="text-xs text-gray-400 dark:text-gray-500">{t('contacts.picker.hint')}</p>
    </div>
  );
}
